/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { finalize, from, Observable } from 'rxjs';
import { SaveAdapter, SerialSaveService } from 'src/save/serial-save.service';
import { PageUnit } from './types';
import { BULB_BUSY_COVER_SERVICE } from '../angularjs.providers';

/**
 * HTTP methods required by the PageView module.
 */
@Injectable({
  providedIn: 'root',
})
export class HttpService {
  constructor(
    private http: HttpClient,
    private serialSaveService: SerialSaveService,
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService: any,
  ) { }

  updateUnit(unitId: string, unitPatch: any): Observable<PageUnit> {
    return from(this.serialSaveService.save(
      {
        save: () => this.http
          .post(`units/${unitId}/update`, unitPatch)
          .toPromise(),
      } as SaveAdapter,
    )) as Observable<PageUnit>;
  }

  /**
   * Method used to publish unit on server.
   * @param unitId unit Id
   */
  publishUnit(unitId: string) {
    return this.http.post(`units/${unitId}/publish`, {}).toPromise();
  }

  deleteTag(tagId: string) {
    const stopBusyIndicatorCallback = this.bulbBusyCoverService.showBusy();

    return this.http.delete(`tags/${tagId}`)
      .pipe(
        finalize(() => {
          stopBusyIndicatorCallback();
        }),
      );
  }

  removeSelectedTag(unitId: string, tagId: string) {
    const stopBusyIndicatorCallback = this.bulbBusyCoverService.showBusy();

    return this.http.delete(`tags/${unitId}/use/${tagId}`)
      .pipe(
        finalize(() => {
          stopBusyIndicatorCallback();
        }),
      );
  }

  selectedTag(unitId: string, tagId: string) {
    const stopBusyIndicatorCallback = this.bulbBusyCoverService.showBusy();

    return this.http.post(`tags/${unitId}/use/${tagId}`, {})
      .pipe(
        finalize(() => {
          stopBusyIndicatorCallback();
        }),
      );
  }

  loadTagsForUser() {
    const stopBusyIndicatorCallback = this.bulbBusyCoverService.showBusy();

    return this.http.get('tags/')
      .pipe(
        finalize(() => {
          stopBusyIndicatorCallback();
        }),
      );
  }
}
